import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/catch';
import { of } from 'rxjs/observable/of';
import { type Store } from 'redux';
import { type ActionsObservable } from 'redux-observable';
import { fetchFailed, fetchSuccess } from '../../common/fetch-request-analytics';
import {
	getCloudId,
	getConfluenceState,
	getParentProduct,
	isSiteAdmin,
} from '../context/selectors';
import { getProject } from '../project/selectors';
import { type ProvisioningStatus, planConfluenceActivation$ } from './requests';
import { PLAN_ACTIVATION, updateConfluenceActivationStatus } from '../actions/activate-confluence';
import { getActivationStatus } from '../activation/selectors';
import { type State } from '../types';
import { type ActivationStatus } from '../activation/types';
import { getTargetingGate, getExperimentValue } from '../../common/feature-gates';
import { UPDATE_CONFLUENCE_STATE } from '../actions';
import {
	CONFLUENCE_ACTIVATING,
	CONFLUENCE_ACTIVE,
	CONFLUENCE_ERROR,
	CONFLUENCE_LOADING,
} from '../context/types';
import { JSW_PARENT_PRODUCT } from '../../common/constants/parent-products';
import { sendTrackAnalyticsEvent } from '../../common/analytics/analytics-web-client';

const provisioningStatusToActivationStatusMap: Record<ProvisioningStatus, ActivationStatus> = {
	PROVISIONING_IN_PROGRESS: 'activating',
	PROVISIONING_FAILED: 'error',
	PROVISIONING_COMPLETED: 'completed',
	PROVISIONING_PROCESS_NOT_FOUND: 'not-allowed',
};

export default (
	action$: ActionsObservable<ReturnType<typeof planConfluenceActivation$>>,
	store: Store<State>,
) =>
	action$
		.ofType(PLAN_ACTIVATION, UPDATE_CONFLUENCE_STATE)
		.filter(() => {
			const state = store.getState();
			const activationStatus = getActivationStatus(state);
			const project = getProject(state);
			const cloudId = getCloudId(state);
			const confluenceState = getConfluenceState(state);

			return (
				!!project &&
				!!cloudId &&
				confluenceState !== CONFLUENCE_LOADING &&
				activationStatus === 'not-started'
			);
		})
		.switchMap(() => {
			const state = store.getState();
			const confluenceState = getConfluenceState(state);

			if (
				!getTargetingGate('jsw_project_pages_on_demand_provisioning') ||
				[CONFLUENCE_ACTIVE, CONFLUENCE_ACTIVATING, CONFLUENCE_ERROR].includes(confluenceState)
			) {
				return of(
					updateConfluenceActivationStatus({
						status: 'not-allowed',
					}),
				);
			}

			const isAdmin = isSiteAdmin(state);

			if (!getTargetingGate('project-pages-on-demand-tel-gate') && !isAdmin) {
				return of(
					updateConfluenceActivationStatus({
						status: 'not-allowed',
					}),
				);
			}

			const project = getProject(state);
			const cloudId = getCloudId(state);

			return planConfluenceActivation$(cloudId, {
				projectId: project.id,
			})
				.mergeMap((response) => {
					fetchSuccess('confluence.activate.plan');
					if (response.allowed) {
						// For attribution of Project Pages On Demand Biz experiment: https://hello.atlassian.net/wiki/spaces/Growth/pages/4247292714/Project+Pages+on-demand+provisioning+for+admins+in+biz+projects
						if (getParentProduct(state) !== JSW_PARENT_PRODUCT && isAdmin) {
							sendTrackAnalyticsEvent({
								source: '-activate-confluence-plan',
								action: 'exposed',
								actionSubject: 'feature',
								attributes: {
									experiment: 'jsw_project_pages_on_demand_biz',
								},
							});
						}

						if (getTargetingGate('project-pages-on-demand-tel-gate-aa') && !isAdmin) {
							getExperimentValue('project-pages-on-demand-tel-aa', 'not-enrolled', true);
						}

						if (getTargetingGate('project-pages-on-demand-tel-gate') && !isAdmin) {
							const cohort = getExperimentValue(
								'project-pages-on-demand-tel',
								'not-enrolled',
								true,
							);
							if (cohort === 'variation') {
								return of(
									updateConfluenceActivationStatus({
										status: 'allowed',
									}),
								);
							} else {
								return of(
									updateConfluenceActivationStatus({
										status: 'not-allowed',
									}),
								);
							}
						}

						return of(
							updateConfluenceActivationStatus({
								status: 'allowed',
							}),
						);
					}
					return of(
						updateConfluenceActivationStatus({
							status:
								provisioningStatusToActivationStatusMap[
									response.context?.status || 'PROVISIONING_PROCESS_NOT_FOUND'
								],
						}),
					);
				})
				.catch((error) => {
					fetchFailed('confluence.activate.plan', error);
					return of(
						updateConfluenceActivationStatus({
							status: 'not-allowed',
						}),
					);
				});
		});
